import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { GenericResource, GraphQuery, ResourceType } from '@/types';
import {
  UseExtendInfiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
} from '@/utils/api';

export const useGenericSearch = (
  props: {
    query?: string | GraphQuery;
    label?: ResourceType;
    desc?: boolean;
    global?: boolean;
    exact?: boolean;
    seedDomains?: boolean;
    seedIps?: boolean;
    tree?: boolean;
    allTenants?: boolean;
  },
  options?: UseExtendInfiniteQueryOptions<GenericResource>
) => {
  const { query, desc, exact, label, allTenants } = props;

  const axios = useAxios({
    email: options?.email,
  });

  // Opt-in keyset (cursor) pagination — mirrors useMy. Off (default) = legacy
  // offset paging, byte-identical to before. On = page by an opaque cursor
  // token, falling back to offset paging when the backend can't engage keyset.
  //
  // Scope: cursor forwarding is wired for both POST graph-query requests and the
  // string-key GET branch. Both send `cursor: ''` on page 1, resume via the
  // echoed token, and fall back to offset paging when the backend emits no
  // cursor.
  const cursorPagination = options?.cursorPagination === true;

  const response = useInfiniteQuery<GenericResource>({
    ...options,
    defaultErrorMessage: `Failed to fetch search result`,
    retry: false,
    queryKey: [
      ...getQueryKey.genericSearch(
        JSON.stringify(props.query),
        desc,
        props.global,
        options?.email,
        allTenants,
        label,
        exact,
        props.tree
      ),
      // Keep cursor-mode pages in a distinct cache bucket so toggling
      // cursorPagination never co-mingles offset and cursor pages (mirrors useMy).
      ...(cursorPagination ? ['cursor'] : []),
    ],
    enabled: options?.enabled ?? Boolean(query),
    queryFn: async ({ pageParam }) => {
      // Cursor mode page param is `{ offset, cursor }`; see useMy for the full
      // rationale. When cursor mode is off, `offsetValue === pageParam`, so the
      // request is byte-identical to before.
      const cursorValue =
        cursorPagination &&
        typeof (pageParam as { cursor?: unknown } | undefined)?.cursor ===
          'string'
          ? (pageParam as { cursor: string }).cursor
          : undefined;
      const offsetValue = cursorPagination
        ? (pageParam as { offset?: unknown } | undefined)?.offset
        : pageParam;

      const { data } =
        typeof props.query === 'object'
          ? await axios({
              method: 'POST',
              url: '/my',
              params: {
                tree: props.tree,
                allTenants,
              },
              data: {
                ...props.query,
                ...(cursorValue !== undefined
                  ? { cursor: cursorValue }
                  : { page: offsetValue ? offsetValue : undefined }),
              },
            })
          : await axios.get<GenericResource>(`/my`, {
              params: {
                label: label,
                key: query,
                desc,
                exact: exact,
                // Mirror the POST either/or (line 84) and useMy's GET branch:
                // when a keyset cursor is present send it as the RAW string the
                // backend reads from `?cursor=` (offset is JSON-stringified,
                // cursor is not) and omit offset; otherwise send offset exactly as
                // legacy. `cursorValue` is `''` on page 1 (the guard is
                // `!== undefined`, preserving the empty-string sentinel) and
                // `undefined` when the flag is off — keeping flag-off requests
                // byte-identical. SEC-5 query-string risk acceptance: see useMy.
                ...(cursorValue !== undefined
                  ? { cursor: cursorValue }
                  : {
                      offset: offsetValue
                        ? JSON.stringify(offsetValue)
                        : undefined,
                    }),
                global: props.global,
                allTenants,
              },
            });

      return {
        data,
        offset: (data as any).offset, // eslint-disable-line @typescript-eslint/no-explicit-any
        count: (data as any).count, // eslint-disable-line @typescript-eslint/no-explicit-any
        cursor: (data as any).cursor, // eslint-disable-line @typescript-eslint/no-explicit-any
      };
    },
    initialPageParam: cursorPagination
      ? { offset: undefined, cursor: '' }
      : undefined,
    getNextPageParam: lastPage => {
      const offset = lastPage?.offset;
      const count = lastPage?.count ?? 0;
      if (cursorPagination) {
        // Keyset mode: prefer the echoed cursor; fall back to offset when the
        // backend couldn't engage keyset. Graph responses never carry `count`
        // (only DynamoDB TableSearch does) and the backend emits a next offset
        // only when the page came back full, so — mirroring useMy — continue
        // whenever a next offset is present and stop only when neither a next
        // cursor nor a next offset is.
        const cursor = lastPage?.cursor;
        if (cursor) return { offset: undefined, cursor };
        return offset ? { offset, cursor: undefined } : undefined;
      }
      // Legacy offset flow — byte-identical to before.
      if (typeof offset === 'number') {
        return offset < count ? offset : undefined;
      }
      return offset;
    },
  });

  const processedData = useMemo(() => {
    if (!response.data) return undefined;

    return response.data.pages.reduce(
      (acc, data) => {
        const seeds = data?.['data']?.['seeds'] as any; // eslint-disable-line @typescript-eslint/no-explicit-any

        // Grouping for assets
        const dataAssets = data?.['data']?.['assets'] || [];
        const addomains = (data?.['data']?.['addomains'] || []) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        const integrations = (data?.['data']?.['integrations'] || []) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        const repositories = (data?.['data']?.['repositories'] || []) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        const webapplications = (data?.['data']?.['webapplications'] ||
          []) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        const awsresources = (data?.['data']?.['awsresources'] || []) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        const azureresources = (data?.['data']?.['azureresources'] ||
          []) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        const gcpresources = (data?.['data']?.['gcpresources'] || []) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        const assets = [
          ...dataAssets,
          ...addomains,
          ...integrations,
          ...repositories,
          ...webapplications,
          ...awsresources,
          ...azureresources,
          ...gcpresources,
        ];

        return {
          accounts: [
            ...acc['accounts'],
            ...(data?.['data']?.['accounts'] || []),
          ],
          assets: [...acc['assets'], ...assets],
          attributes: [
            ...acc['attributes'],
            ...(data?.['data']?.['attributes'] || []),
          ],
          conditions: [
            ...acc['conditions'],
            ...(data?.['data']?.['conditions'] || []),
          ],
          files: [...acc['files'], ...(data?.['data']?.['files'] || [])],
          webpages: [
            ...acc['webpages'],
            ...(data?.['data']?.['webpages'] || []),
          ],
          keies: [...acc['keies'], ...(data?.['data']?.['keies'] || [])],
          accesss: [...acc['accesss'], ...(data?.['data']?.['accesss'] || [])],
          jobs: [...acc['jobs'], ...(data?.['data']?.['jobs'] || [])],
          risks: [...acc['risks'], ...(data?.['data']?.['risks'] || [])],
          flags: [...acc['flags'], ...(data?.['data']?.['flags'] || [])],
          seeds: [...acc['seeds'], ...(seeds ? seeds : [])],
          statistics: [
            ...acc['statistics'],
            ...(data?.['data']?.['statistics'] || []),
          ],
          preseeds: [
            ...acc['preseeds'],
            ...(data?.['data']?.['preseeds'] || []),
          ],
          addomains: [...acc['addomains'], ...addomains],
          integrations: [...acc['integrations'], ...integrations],
          repositories: [...acc['repositories'], ...repositories],
          webapplications: [...acc['webapplications'], ...webapplications],
          awsresources: [...acc['awsresources'], ...awsresources],
          azureresources: [...acc['azureresources'], ...azureresources],
          gcpresources: [...acc['gcpresources'], ...gcpresources],
          configurations: [
            ...acc['configurations'],
            ...(data?.['data']?.['configurations'] || []),
          ],
        };
      },
      {
        accounts: [],
        assets: [],
        attributes: [],
        conditions: [],
        files: [],
        webpages: [],
        webapplications: [],
        jobs: [],
        risks: [],
        seeds: [],
        statistics: [],
        preseeds: [],
        flags: [],
        keies: [],
        accesss: [],
        addomains: [],
        integrations: [],
        repositories: [],
        awsresources: [],
        azureresources: [],
        gcpresources: [],
        configurations: [],
      } as GenericResource
    );
  }, [response.data, props.seedDomains, props.seedIps]);

  return {
    ...response,
    data: processedData,
  };
};

export const useGenericSearchMutation = () => {
  const axios = useAxios();

  return useMutation({
    defaultErrorMessage: 'Failed to fetch generic search',
    mutationFn: async (key: string) => {
      const { data } = await axios.get('/my', {
        params: {
          key,
        },
      });
      return data;
    },
  });
};
